import { Context, createContext, useContext, useMemo } from 'react';

import { Optional } from '@vimeo/core/types/generic';

import { browserConfig } from '../../configs/BrowserConfig';
import { ESwipeDirection } from '../../hooks/dom/useSwipe';
import { CallablePlaceholder } from '../../types/general';
import {
  IResponsiveConfig,
  TConfigProp,
  IResponsiveSidebarContext,
  ESidebarAttach,
} from './ResponsiveSidebar.types';

export const ResponsiveSidebarContext: Context<IResponsiveSidebarContext> =
  createContext<IResponsiveSidebarContext>({
    isHeaderEnabled: false,
    setIsHeaderEnabled: CallablePlaceholder,
  });

export function useResponsiveSidebarContext(): IResponsiveSidebarContext {
  return useContext(ResponsiveSidebarContext);
}

export function useSidebarPropConfigValue<T>(propConfig: TConfigProp<T>): T {
  return useMemo(() => {
    if (typeof propConfig === 'object') {
      return (
        (browserConfig.BROWSER?.isMobile && (propConfig as IResponsiveConfig<T>).mobile) ||
        (propConfig as IResponsiveConfig<T>).web
      );
    } else {
      return propConfig;
    }
  }, [propConfig]);
}

export interface ISidebarSwipeDirection {
  swipeSameDirection: Optional<ESwipeDirection>;
  swipeOppositeDirection: Optional<ESwipeDirection>;
}

export function useSidebarSwipeDirection(
  sidebarAttach: ESidebarAttach,
  isPortrait: boolean,
): ISidebarSwipeDirection {
  return useMemo(() => {
    if (!sidebarAttach) {
      return {
        swipeSameDirection: null,
        swipeOppositeDirection: null,
      };
    }

    if (sidebarAttach === ESidebarAttach.TOP) {
      return {
        swipeSameDirection: ESwipeDirection.UP,
        swipeOppositeDirection: ESwipeDirection.DOWN,
      };
    }

    if (
      sidebarAttach === ESidebarAttach.BOTTOM ||
      (sidebarAttach === ESidebarAttach.RIGHT && isPortrait) ||
      (sidebarAttach === ESidebarAttach.LEFT && isPortrait)
    ) {
      return {
        swipeSameDirection: ESwipeDirection.DOWN,
        swipeOppositeDirection: ESwipeDirection.UP,
      };
    }

    if (sidebarAttach === ESidebarAttach.RIGHT) {
      return {
        swipeSameDirection: ESwipeDirection.RIGHT,
        swipeOppositeDirection: ESwipeDirection.LEFT,
      };
    }

    if (sidebarAttach === ESidebarAttach.LEFT) {
      return {
        swipeSameDirection: ESwipeDirection.LEFT,
        swipeOppositeDirection: ESwipeDirection.RIGHT,
      };
    }

    return {
      swipeSameDirection: null,
      swipeOppositeDirection: null,
    };
  }, [sidebarAttach, isPortrait]);
}
